import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/site/Seo";
import ConceptTag from "@/components/site/ConceptTag";
import { CtaLink } from "@/components/site/primitives";
import { JOURNAL_POSTS } from "@/lib/content";

export default function JournalArticle() {
  const { slug } = useParams();
  const post = JOURNAL_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-[72px] text-center">
        <Seo title="Not Found — RestPower Journal" description="This journal entry could not be found." path="/journal" />
        <h1 className="font-display text-4xl font-light uppercase tracking-tight">Entry not found</h1>
        <p className="mt-4 text-white/55">The journal entry you're looking for doesn't exist.</p>
        <div className="mt-8"><CtaLink to="/journal">Back to Journal</CtaLink></div>
      </div>
    );
  }

  return (
    <>
      <Seo title={`${post.title} — RestPower Journal`} description={post.excerpt} path={`/journal/${post.slug}`} />
      <article className="bg-rp-bg pt-[72px]" data-testid="journal-article">
        <div className="relative h-[46vh] min-h-[320px] w-full overflow-hidden">
          <img src={post.image} alt={post.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-rp-bg via-rp-bg/50 to-black/40" />
          {post.concept && <ConceptTag className="absolute right-6 top-6 md:right-12" />}
        </div>

        <div className="mx-auto max-w-3xl px-6 py-14 md:py-20">
          <Link to="/journal" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-white/50 transition-colors hover:text-rp-accent" data-testid="back-to-journal">
            <ArrowLeft className="h-4 w-4" /> Back to Journal
          </Link>

          <div className="mt-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.14em] text-white/40">
            <span className="text-rp-accent">{post.category}</span>
            <span>·</span>
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>

          <h1 className="mt-5 font-display text-4xl font-light uppercase leading-[0.98] tracking-tight md:text-5xl">
            {post.title}
          </h1>

          <div className="mt-10 space-y-6">
            {post.body.map((block, i) =>
              block.type === "h" ? (
                <h2 key={i} className="pt-4 font-display text-2xl font-medium tracking-tight text-white">{block.text}</h2>
              ) : (
                <p key={i} className="text-lg leading-relaxed text-white/65">{block.text}</p>
              )
            )}
          </div>

          <div className="mt-12 rounded-2xl border border-rp-accent/20 bg-rp-card p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-rp-accent">Concept Content</p>
            <p className="mt-2 text-sm leading-relaxed text-white/55">
              RestPower is an early-stage company in active development. This journal entry reflects our
              current thinking and vision. It does not describe operating locations, signed deals, or
              completed developments.
            </p>
          </div>
        </div>
      </article>
    </>
  );
}
